import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Button, Spin, Tooltip} from 'antd';
import {
    DownloadOutlined,
    FileSearchOutlined,
    MinusOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {getReferenceReviewViewerSource} from '../../../Services/stage.service';
import {download_referentiel_document} from '../../../Services/referentiel.service';
import ContractViewerV2 from './ContractViewerV2';

const canonicalQuote = (evidence = {}) => String(
    evidence.citation
    || evidence.exact_quote
    || evidence.quote
    || evidence.text
    || '',
).trim();

const canonicalPage = (evidence = {}) => {
    const value = evidence.page ?? evidence.page_number ?? evidence.location?.page;
    const page = Number(value);
    return Number.isFinite(page) && page >= 1 ? page : null;
};

const canonicalBbox = (evidence = {}) => {
    const bbox = evidence.bbox || evidence.bounding_box || evidence.location?.bbox;
    if (!bbox || typeof bbox !== 'object') return null;
    const normalized = {
        left: Number(bbox.left ?? bbox.Left),
        top: Number(bbox.top ?? bbox.Top),
        width: Number(bbox.width ?? bbox.Width),
        height: Number(bbox.height ?? bbox.Height),
    };
    return Object.values(normalized).every(Number.isFinite)
        && normalized.width > 0
        && normalized.height > 0
        ? normalized
        : null;
};

const getViewerErrorTranslationKey = (error) => {
    const code = String(error?.message || error || '');
    if (code.endsWith('_403')) return 'referenceViewer.accessError';
    if (code.endsWith('_404')) return 'referenceViewer.notFoundError';
    return 'referenceViewer.loadError';
};

const downloadFromTrustedUrl = (url, filename) => {
    if (!url) return false;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener noreferrer';
    if (filename) anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return true;
};

const TextReferenceView = ({text, navigationRequest}) => {
    const {t} = useTranslation('review');
    const [scale, setScale] = useState(1);
    const markRef = useRef(null);
    const scrollRef = useRef(null);
    const quote = canonicalQuote(navigationRequest?.evidence);
    const exactIndex = quote ? String(text || '').indexOf(quote) : -1;

    useEffect(() => {
        if (!navigationRequest?.requestId) return;
        if (exactIndex >= 0 && markRef.current) {
            markRef.current.scrollIntoView?.({behavior: 'smooth', block: 'center'});
        } else if (scrollRef.current) {
            scrollRef.current.scrollTo?.({top: 0, behavior: 'smooth'});
        }
    }, [exactIndex, navigationRequest?.requestId]);

    const parts = exactIndex >= 0 ? {
        before: text.slice(0, exactIndex),
        match: text.slice(exactIndex, exactIndex + quote.length),
        after: text.slice(exactIndex + quote.length),
    } : null;

    return (
        <div className="reference-text-view">
            <div className="reference-text-view__zoom" aria-label={t('referenceViewer.zoom')}>
                <Button type="text" size="small" icon={<MinusOutlined />} onClick={() => setScale((value) => Math.max(.8, value - .1))} />
                <span>{Math.round(scale * 100)}%</span>
                <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => setScale((value) => Math.min(1.6, value + .1))} />
            </div>
            {navigationRequest?.requestId && exactIndex < 0 && (
                <Alert className="reference-viewer__notice" type="info" showIcon message={t('referenceViewer.locationUnavailable')} />
            )}
            <div className="reference-text-view__scroll" ref={scrollRef}>
                <article className="reference-text-view__page" style={{fontSize: `${scale}rem`}}>
                    {parts ? <><span>{parts.before}</span><mark ref={markRef}>{parts.match}</mark><span>{parts.after}</span></> : text}
                </article>
            </div>
        </div>
    );
};

export default function ReferenceDocumentViewer({source, navigationRequest}) {
    const {t} = useTranslation('review');
    const [payload, setPayload] = useState(null);
    const [status, setStatus] = useState('idle');
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const analysisId = source?.analysisId;
    const documentVersionId = source?.documentVersionId;
    const referenceFileId = source?.referenceFileId;
    const reviewId = source?.reviewId;
    const snapshotId = source?.snapshotId;
    const sourceIdentity = [
        analysisId,
        documentVersionId,
        referenceFileId,
        reviewId,
        snapshotId,
    ].join(':');
    const sourceRequestRef = useRef(null);
    sourceRequestRef.current = {analysisId, documentVersionId, referenceFileId, reviewId, snapshotId};

    useEffect(() => {
        const request = sourceRequestRef.current || {};
        if (!request.analysisId || !request.documentVersionId || !request.referenceFileId || !request.reviewId) {
            setPayload(null);
            setStatus('idle');
            setLoadError(null);
            return undefined;
        }
        const controller = new AbortController();
        setPayload(null);
        setStatus('loading');
        setLoadError(null);
        getReferenceReviewViewerSource({
            analyseId: request.analysisId,
            analysisId: request.analysisId,
            documentVersionId: request.documentVersionId,
            referenceFileId: request.referenceFileId,
            reviewId: request.reviewId,
            snapshotId: request.snapshotId,
            signal: controller.signal,
        })
            .then((nextPayload) => {
                if (controller.signal.aborted) return;
                setPayload(nextPayload);
                setStatus('ready');
            })
            .catch((error) => {
                if (!controller.signal.aborted) {
                    setLoadError(error);
                    setStatus('error');
                }
            });
        return () => controller.abort();
    }, [reloadKey, sourceIdentity]);

    const scopedDownloadUrl = payload?.download_url
        || source?.downloadUrl
        || source?.download_url;
    const canUseRepositoryDownload = Boolean(payload)
        && payload.download_available === true
        && Boolean(referenceFileId);
    const canDownloadOriginal = Boolean(scopedDownloadUrl) || canUseRepositoryDownload;
    const downloadOriginal = () => {
        if (!canDownloadOriginal) return;
        if (downloadFromTrustedUrl(scopedDownloadUrl, payload?.title || source?.title)) return;
        download_referentiel_document(referenceFileId, payload?.title || source?.title);
    };

    const evidence = useMemo(() => navigationRequest?.evidence || {}, [navigationRequest?.evidence]);
    const page = canonicalPage(evidence);
    const bbox = canonicalBbox(evidence);
    const evidenceRef = navigationRequest?.requestId ? `reference-evidence:${navigationRequest.requestId}` : null;
    const highlightBlocks = useMemo(() => (
        evidenceRef && page && bbox
            ? [{ref: evidenceRef, page, bbox, text: canonicalQuote(evidence), kind: 'reference_evidence'}]
            : []
    ), [bbox, evidence, evidenceRef, page]);
    const exactPdfLocation = Boolean(page || (page && bbox));

    if (status === 'loading') return <div className="reference-viewer__state"><Spin /><span>{t('referenceViewer.loading')}</span></div>;
    if (status === 'error') return (
        <div className="reference-viewer__state reference-viewer__state--error">
            <Alert
                className="reference-viewer__error-alert"
                type="warning"
                showIcon
                message={t(getViewerErrorTranslationKey(loadError))}
            />
            <div className="reference-viewer__error-actions">
                <Button
                    icon={<ReloadOutlined aria-hidden="true" />}
                    onClick={() => setReloadKey((current) => current + 1)}
                >
                    {t('referenceViewer.retry')}
                </Button>
                {canDownloadOriginal && (
                    <Button icon={<DownloadOutlined aria-hidden="true" />} onClick={downloadOriginal}>
                        {t('referenceViewer.downloadOriginal')}
                    </Button>
                )}
            </div>
        </div>
    );
    if (!payload) return null;

    return (
        <div className="reference-viewer">
            <header className="reference-viewer__header">
                <div className="reference-viewer__identity"><FileSearchOutlined /><span title={payload.title}>{payload.title}</span></div>
                {canDownloadOriginal && (
                    <Tooltip title={t('referenceViewer.downloadOriginal')}>
                        <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined aria-hidden="true" />}
                            onClick={downloadOriginal}
                        >
                            {t('referenceViewer.downloadOriginal')}
                        </Button>
                    </Tooltip>
                )}
            </header>
            {payload.render_mode === 'pdf' ? (
                <div className="reference-viewer__pdf">
                    {navigationRequest?.requestId && !exactPdfLocation && (
                        <Alert className="reference-viewer__notice" type="info" showIcon message={t('referenceViewer.locationUnavailable')} />
                    )}
                    <ContractViewerV2
                        fileUrl={payload.view_url}
                        textractBlocks={highlightBlocks}
                        selectedRef={evidenceRef}
                        requestedPage={page}
                        navigationKey={navigationRequest?.requestId}
                    />
                </div>
            ) : (
                <TextReferenceView text={payload.text_content || ''} navigationRequest={navigationRequest} />
            )}
        </div>
    );
}

export {canonicalBbox, canonicalPage, canonicalQuote};
